import directorApi from "./api";

const candidates = (user) => [...new Set(
  (Array.isArray(user?.productProfiles) ? user.productProfiles : [])
    .filter((profile) => profile?.state === "active" && profile?.companyId)
    .map((profile) => String(profile.companyId)),
)];

export const bootstrapDirector = async ({
  user,
  preferredCompanyId = null,
  dispatch,
  api = directorApi,
}) => {
  dispatch({ type: "BOOTSTRAP_REQUEST" });
  try {
    const accessByCompany = {};
    for (const companyId of candidates(user)) {
      try {
        const access = await api.fetchDirectorAccess(companyId);
        if (
          access?.companyId === companyId
          && Array.isArray(access.capabilities)
          && access.capabilities.includes("director.view")
        ) accessByCompany[companyId] = access;
      } catch (error) {
        if (error?.status !== 403) throw error;
      }
    }
    const preferred = preferredCompanyId === null || preferredCompanyId === undefined
      ? null
      : String(preferredCompanyId);
    const selectedCompanyId = preferred && accessByCompany[preferred]
      ? preferred
      : Object.keys(accessByCompany)[0] ?? null;
    const metadata = selectedCompanyId
      ? await api.fetchDirectorMetadata(selectedCompanyId)
      : null;
    dispatch({
      type: "BOOTSTRAP_SUCCESS",
      accessByCompany,
      selectedCompanyId,
      metadata,
    });
  } catch (error) {
    dispatch({ type: "BOOTSTRAP_FAILURE", error: {
      status: error?.status ?? 0,
      code: error?.code ?? "bootstrap_failed",
      message: error?.message ?? "Não foi possível validar o acesso Diretoria.",
      retryable: error?.retryable === true,
    } });
  }
};

const requestFilters = (state, overrides = {}) => ({
  legalEntityId: overrides.legalEntityId ?? state.filters.legalEntityId,
  projectKey: overrides.projectKey ?? state.filters.projectKey,
  costCenterId: overrides.costCenterId ?? state.filters.costCenterId,
  scenario: overrides.scenario ?? state.cashflow?.scenario ?? "base",
  ...(overrides.from ?? state.period?.from
    ? { from: overrides.from ?? state.period.from }
    : {}),
  ...(overrides.to ?? state.period?.to
    ? { to: overrides.to ?? state.period.to }
    : {}),
});

const keyFor = (kind, companyId, filters) => JSON.stringify([kind, companyId, filters]);

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const selected = (overrides, key, fallback) => (
  hasOwn(overrides, key) ? overrides[key] : fallback
);

const customerReceiptFilters = (state, overrides = {}, customerRef = undefined) => {
  const specific = state.receivableFilters ?? {};
  return {
    from: selected(overrides, "from", state.period?.from ?? null),
    to: selected(overrides, "to", state.period?.to ?? null),
    legalEntityId: selected(overrides, "legalEntityId", state.filters?.legalEntityId ?? null),
    projectKey: selected(overrides, "projectKey", state.filters?.projectKey ?? null),
    customerRef: customerRef === undefined
      ? selected(overrides, "customerRef", specific.customerRef ?? null)
      : customerRef,
    unitRef: selected(overrides, "unitRef", specific.unitRef ?? null),
    basis: selected(overrides, "basis", specific.basis ?? "nominal"),
    page: selected(overrides, "page", specific.page ?? 1),
    pageSize: selected(overrides, "pageSize", specific.pageSize ?? 25),
  };
};

const itemVarianceFilters = (state, overrides = {}) => ({
  projectKey: overrides.projectKey ?? state.filters.projectKey,
  status: overrides.status ?? "all",
  cause: overrides.cause ?? null,
  page: overrides.page ?? 1,
  pageSize: overrides.pageSize ?? 50,
});

const createActions = ({
  getState,
  dispatch,
  api = directorApi,
  user,
  preferredCompanyId = null,
}) => ({
  bootstrap: () => bootstrapDirector({ user, preferredCompanyId, dispatch, api }),
  selectCompany: async (companyId) => {
    const state = getState();
    if (!state.accessByCompany[companyId]) return false;
    try {
      const metadata = await api.fetchDirectorMetadata(companyId);
      dispatch({ type: "COMPANY_SELECTED", companyId, metadata });
      return true;
    } catch (error) {
      dispatch({ type: "BOOTSTRAP_FAILURE", error });
      return false;
    }
  },
  setPeriod: (period) => dispatch({ type: "PERIOD_CHANGED", period }),
  setFilters: (filters) => dispatch({ type: "FILTERS_CHANGED", filters }),
  setReceivableFilters: (filters) => dispatch({ type: "RECEIVABLE_FILTERS_CHANGED", filters }),
  loadCashflow: async (overrides = {}) => {
    const state = getState();
    if (!state.selectedCompanyId) return null;
    const filters = requestFilters(state, overrides);
    const requestKey = keyFor("cashflow", state.selectedCompanyId, filters);
    dispatch({ type: "CASHFLOW_REQUEST", requestKey });
    try {
      const data = await api.fetchDirectorCashflow(state.selectedCompanyId, filters);
      dispatch({ type: "CASHFLOW_SUCCESS", requestKey, data });
      return data;
    } catch (error) {
      dispatch({ type: "CASHFLOW_FAILURE", requestKey, error });
      return null;
    }
  },
  loadCashflowBreakdown: async (competence, overrides = {}) => {
    const state = getState();
    if (!state.selectedCompanyId || !/^\d{4}-(0[1-9]|1[0-2])$/.test(competence)) return null;
    const filters = requestFilters(state, { ...overrides, from: competence, to: competence });
    const requestKey = keyFor("cashflowBreakdown", state.selectedCompanyId, filters);
    dispatch({ type: "CASHFLOW_BREAKDOWN_REQUEST", requestKey });
    try {
      const data = await api.fetchDirectorCashflow(state.selectedCompanyId, filters);
      const breakdown = data?.breakdown ?? null;
      dispatch({ type: "CASHFLOW_BREAKDOWN_SUCCESS", requestKey, data: breakdown });
      return breakdown;
    } catch (error) {
      dispatch({ type: "CASHFLOW_BREAKDOWN_FAILURE", requestKey, error });
      return null;
    }
  },
  loadItemVariances: async (overrides = {}) => {
    const state = getState();
    if (!state.selectedCompanyId) return null;
    const filters = itemVarianceFilters(state, overrides);
    if (!filters.projectKey) return null;
    const requestKey = keyFor("itemVariances", state.selectedCompanyId, filters);
    dispatch({ type: "ITEM_VARIANCES_REQUEST", requestKey });
    try {
      const data = await api.fetchDirectorItemVariances(state.selectedCompanyId, filters);
      dispatch({ type: "ITEM_VARIANCES_SUCCESS", requestKey, data });
      return data;
    } catch (error) {
      dispatch({ type: "ITEM_VARIANCES_FAILURE", requestKey, error });
      return null;
    }
  },
  loadCustomerReceipts: async (overrides = {}) => {
    const state = getState();
    if (!state.selectedCompanyId) return null;
    const filters = customerReceiptFilters(state, overrides);
    const requestKey = keyFor("customerReceipts", state.selectedCompanyId, filters);
    dispatch({ type: "CUSTOMER_RECEIPTS_REQUEST", requestKey });
    try {
      const data = await api.fetchCustomerReceipts(state.selectedCompanyId, filters);
      dispatch({ type: "CUSTOMER_RECEIPTS_SUCCESS", requestKey, data });
      return data;
    } catch (error) {
      dispatch({ type: "CUSTOMER_RECEIPTS_FAILURE", requestKey, error });
      return null;
    }
  },
  loadCustomerUnits: async (customerRef, overrides = {}) => {
    if (typeof customerRef !== "string" || !/^[a-f0-9]{64}$/.test(customerRef)) return null;
    const state = getState();
    if (!state.selectedCompanyId) return null;
    const filters = customerReceiptFilters(state, overrides, customerRef);
    const requestKey = keyFor("customerUnits", state.selectedCompanyId, filters);
    dispatch({ type: "CUSTOMER_UNITS_REQUEST", requestKey });
    try {
      const data = await api.fetchCustomerUnits(state.selectedCompanyId, customerRef, filters);
      dispatch({ type: "CUSTOMER_UNITS_SUCCESS", requestKey, data });
      return data;
    } catch (error) {
      dispatch({ type: "CUSTOMER_UNITS_FAILURE", requestKey, error });
      return null;
    }
  },
  clearError: () => dispatch({ type: "ERROR_CLEARED" }),
});

export default createActions;
